/**
 * La pantalla del primer login: elegir contrasena antes de entrar a nada.
 *
 * ===========================================================================
 * NO ES UNA PANTALLA QUE ESCONDE EL RESTO
 *
 * Mientras `debe_cambiar_contrasena` sea true, la API contesta 409 a todo lo
 * demas [migracion 0032]. O sea que no se esconde la aplicacion: la aplicacion
 * no funcionaria. Pintar el menu y las zonas seria enseñar siete puertas que
 * devuelven un error, y el usuario acabaria pensando que la herramienta esta
 * rota el dia que la estrena.
 *
 * Por eso esto ocupa la pantalla entera y no lleva ni menu ni cabecera de
 * navegacion. Lo unico que si lleva es el selector de idioma y el boton de
 * salir: si alguien entro con la cuenta equivocada --le dictaron la contrasena
 * de otro-- tiene que poder irse sin cambiarle la contrasena a un companero.
 *
 * ===========================================================================
 * SE PIDE LA ACTUAL AUNQUE ACABE DE ESCRIBIRLA
 *
 * Y se dice por que, en la pantalla: una sesion abierta en un navegador que
 * alguien dejo sin bloquear es exactamente el caso en el que un tercero cambia
 * la contrasena y se queda con la cuenta. Sin la explicacion, pedirla parece
 * burocracia y quien la lee aprende a desconfiar del resto de los avisos.
 *
 * ===========================================================================
 * LOS TRES CAMPOS SE LEEN AL MANDAR, NO AL TECLEAR
 *
 * Mismo patron que `vista/login.ts`, y por un motivo concreto: un manejador por
 * campo que hace `onCambio({ ...valores, nueva: x })` captura `valores` en el
 * momento en que se CONSTRUYO la vista, no el actual. Con tres campos y sin
 * repintar entre teclas, rellenar el segundo escribe encima de lo que puso el
 * primero y el formulario se manda medio vacio. Lo encontro un recorrido de la
 * pantalla, no el compilador: los tipos cuadraban.
 *
 * Leerlos del DOM al pulsar Guardar no tiene ese problema, y ademas no obliga a
 * repintar, que es lo que tiraria el foco.
 */

import { el } from '../dom';
import type { Traductor } from '../i18n';
import { IDIOMAS, nombreIdioma, type Idioma } from '../i18n';
import type { ErrorApi } from '../api';

export interface ValoresCambio {
  readonly actual: string;
  readonly nueva: string;
  readonly repetida: string;
}

export const CAMBIO_VACIO: ValoresCambio = { actual: '', nueva: '', repetida: '' };

export interface OpcionesCambio {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly nombre: string;
  readonly valores: ValoresCambio;
  readonly minimo: number;
  readonly trabajando: boolean;
  readonly error: ErrorApi | null;
  /** Lo que no cuadra dentro del propio formulario, sin ir al servidor. */
  readonly aviso: string | null;
  readonly onIdioma: (i: Idioma) => void;
  /** Recibe los tres campos LEIDOS DEL DOM al pulsar Guardar. Ver la cabecera:
   *  un manejador por tecla escribiria encima de los otros dos. */
  readonly onGuardar: (v: ValoresCambio) => void;
  readonly onSalir: () => void;
}

function campo(
  id: string,
  etiqueta: string,
  valor: string,
  ayuda?: string,
): { nodo: HTMLElement; control: HTMLInputElement } {
  const control = el('input', {
    clase: 'control',
    attrs: {
      id,
      type: 'password',
      value: valor,
      required: 'required',
      // `new-password` en los tres: sin esto, el gestor del navegador rellena
      // los tres campos con la misma contrasena guardada y quien lo mira cree
      // que ya escribio algo.
      autocomplete: id === 'cambio-actual' ? 'current-password' : 'new-password',
    },
  });

  return {
    control,
    nodo: el('div', { clase: 'campo' }, [
      el('label', { clase: 'etiqueta', texto: etiqueta, attrs: { for: id } }),
      control,
      ayuda === undefined ? null : el('p', { clase: 'ayuda tenue', texto: ayuda }),
    ]),
  };
}

export function pintarCambioObligado(o: OpcionesCambio): HTMLElement {
  const { t, valores: v } = o;

  const selectIdioma = el('select', { clase: 'control control-menor' });
  for (const i of IDIOMAS) {
    selectIdioma.appendChild(
      el('option', { texto: nombreIdioma(i), attrs: { value: i } }),
    );
  }
  selectIdioma.value = o.idioma;
  selectIdioma.addEventListener('change', () => {
    o.onIdioma(selectIdioma.value as Idioma);
  });

  const actual = campo(
    'cambio-actual',
    t('cambio.actual'),
    v.actual,
    // La explicacion va aqui y no en una nota al pie: sin ella, pedir la actual
    // justo despues de haberla escrito parece burocracia.
    t('cambio.actualAyuda'),
  );
  const nueva = campo(
    'cambio-nueva',
    t('cambio.nueva'),
    v.nueva,
    t('cambio.nuevaAyuda', { n: String(o.minimo) }),
  );
  const repetida = campo('cambio-repetida', t('cambio.repetida'), v.repetida);

  const form = el('form', { clase: 'login-form cambio-form' }, [
    actual.nodo,
    nueva.nodo,
    repetida.nodo,
    el('button', {
      clase: 'boton boton-primario boton-ancho',
      texto: o.trabajando ? t('cambio.guardando') : t('cambio.guardar'),
      attrs: o.trabajando ? { type: 'submit', disabled: 'disabled' } : { type: 'submit' },
    }),
  ]);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    // Las contrasenas NO se recortan: un espacio al principio o al final es
    // parte de ellas, y quitarlo aqui cambiaria la que se guarda sin decirlo.
    if (!o.trabajando) {
      o.onGuardar({
        actual: actual.control.value,
        nueva: nueva.control.value,
        repetida: repetida.control.value,
      });
    }
  });

  return el('div', { clase: 'login' }, [
    el('section', { clase: 'panel login-panel' }, [
      el('h1', { texto: t('cambio.titulo') }),
      el('p', { texto: t('cambio.hola', { nombre: o.nombre }) }),
      el('p', { clase: 'tenue', texto: t('cambio.porQue') }),

      o.error === null
        ? null
        : el('div', { clase: 'aviso aviso-error' }, [
            el('p', { texto: o.error.message }),
          ]),
      o.aviso === null ? null : el('p', { clase: 'aviso aviso-error', texto: o.aviso }),

      form,

      el('div', { clase: 'login-pie' }, [
        el('label', { clase: 'etiqueta-menor', texto: t('app.idioma') }),
        selectIdioma,
        // Para quien entro con la cuenta de otro. Sin esto, la unica salida
        // seria cambiarle la contrasena a un companero.
        el('button', {
          clase: 'boton boton-plano',
          texto: t('cambio.noSoyYo'),
          attrs: { type: 'button' },
          onClick: o.onSalir,
        }),
      ]),
    ]),
  ]);
}

export function enfocarCambio(raiz: HTMLElement): void {
  raiz.querySelector<HTMLInputElement>('#cambio-actual')?.focus();
}
